import { useEffect, useRef, useState } from 'react';

import { getCampaignEventsDetail } from 'services/MailingService';
import { errorToast } from 'utils/toast';
import { getLiteral } from 'utils/getLiteral';

const normalizeNumber = (value) => {
    const parsed = Number(value);
    return Number.isFinite(parsed) ? Math.max(0, Math.trunc(parsed)) : 0;
};

const normalizeScorecards = (scorecards = {}) => ({
    uniqueOpens: normalizeNumber(scorecards.uniqueOpens),
    totalOpens: normalizeNumber(scorecards.totalOpens),
    uniqueClicks: normalizeNumber(scorecards.uniqueClicks),
    totalClicks: normalizeNumber(scorecards.totalClicks),
    canceledSubscriptions: normalizeNumber(scorecards.canceledSubscriptions),
});

const normalizeLinkPerformance = (links = []) => {
    if (!Array.isArray(links)) {
        return [];
    }
    return links
        .filter((link) => typeof link?.url === 'string' && link.url.length > 0)
        .map((link) => {
            const uniqueClicks = normalizeNumber(link.uniqueClicks);
            const rawPercentage = Number(link.clickPercentage);
            const clickPercentage = Number.isFinite(rawPercentage)
                ? Math.max(0, Math.min(100, rawPercentage))
                : 0;
            return {
                url: link.url,
                uniqueClicks,
                clickPercentage,
            };
        });
};

export const useCampaignSummary = ({ campaignId, isActive }) => {
    const [data, setData] = useState(null);
    const [isLoading, setIsLoading] = useState(false);
    const requestGenerationRef = useRef(0);

    useEffect(() => {
        if (!isActive || campaignId == null || campaignId === '') {
            setData(null);
            setIsLoading(false);
            return undefined;
        }

        const generation = ++requestGenerationRef.current;
        setIsLoading(true);
        setData(null);

        getCampaignEventsDetail(campaignId)
            .then((response) => {
                if (generation !== requestGenerationRef.current) {
                    return;
                }
                const normalized = {
                    campaign: response?.campaign ?? null,
                    engagementScorecards: normalizeScorecards(response?.engagementScorecards),
                    engagementFunnel: response?.engagementFunnel ?? null,
                    deliveryHealth: response?.deliveryHealth ?? null,
                    linkPerformance: normalizeLinkPerformance(response?.linkPerformance),
                };
                setData(normalized);
            })
            .catch(() => {
                if (generation !== requestGenerationRef.current) {
                    return;
                }
                errorToast({ text: getLiteral('error_generalerror') });
                setData(null);
            })
            .finally(() => {
                if (generation === requestGenerationRef.current) {
                    setIsLoading(false);
                }
            });

        return () => {
            const current = requestGenerationRef.current;
            requestGenerationRef.current = current + 1;
        };
    }, [isActive, campaignId]);

    return {
        data,
        isLoading,
        campaign: data?.campaign ?? null,
        engagementScorecards: data?.engagementScorecards ?? null,
        engagementFunnel: data?.engagementFunnel ?? null,
        deliveryHealth: data?.deliveryHealth ?? null,
        linkPerformance: data?.linkPerformance ?? [],
    };
};
